import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  getPendingMutations,
  removeMutation,
  updateMutation,
  enqueueMutation,
  clearAllMutations,
  QUEUE_CHANGED_EVENT,
} from '../utils/offlineQueue';
import { revertMutationInMirror, applyMutationToMirror } from '../utils/offlineSnapshot';
import { recordSyncEvents, removeSyncEventAt } from '../utils/syncHistory';
import { API_BASE_URL } from '../utils/apiBase';

const BACKEND_URL = API_BASE_URL;
// Give up on a change that keeps failing (non-conflict, e.g. repeated
// 5xx) after this many attempts so a single broken entry can't block
// the queue forever.
const MAX_ATTEMPTS = 5;
// Quiet background retry cadence while online with a backlog.
const AUTO_RETRY_MS = 3 * 60 * 1000;
// How long the "All changes synced" confirmation lingers.
const SYNCED_CONFIRM_MS = 3500;

// Rebuild the {op, plantId, ids, payload} shape from a stored entry.
function mutationFromEntry(entry) {
  return { op: entry.op, plantId: entry.plant_id, ids: entry.ids, payload: entry.data };
}

/**
 * Manages the offline mutation queue for Drive-mode users: tracks the
 * pending list, auto-flushes on reconnect + on a quiet timer, caps
 * retries, supports discard (with undo), records a sync-history log, and
 * exposes flush progress + a brief "just synced" confirmation.
 */
export function useOfflineQueue(userKey, enabled, onSynced, labels = {}) {
  const [pending, setPending] = useState([]);
  const labelsRef = useRef(labels);
  labelsRef.current = labels;
  const [flushing, setFlushing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const flushingRef = useRef(false);
  const syncedTimerRef = useRef(null);
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;

  const refresh = useCallback(async () => {
    if (!enabled || !userKey) { setPending([]); return; }
    setPending(await getPendingMutations(userKey));
  }, [enabled, userKey]);

  const showSyncedConfirm = useCallback(() => {
    setJustSynced(true);
    if (syncedTimerRef.current) clearTimeout(syncedTimerRef.current);
    syncedTimerRef.current = setTimeout(() => setJustSynced(false), SYNCED_CONFIRM_MS);
  }, []);

  const flush = useCallback(async () => {
    if (!enabled || !userKey) return;
    if (flushingRef.current) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    // Claim the flush lock SYNCHRONOUSLY before any await so two
    // near-simultaneous 'online' events can't both start flushing and
    // replay every queued request twice (destructive for bulk ops).
    flushingRef.current = true;
    const entries = await getPendingMutations(userKey);
    if (entries.length === 0) { flushingRef.current = false; return; }

    setFlushing(true);
    setProgress({ done: 0, total: entries.length });
    const sessionToken = localStorage.getItem('auth_hint');
    let synced = 0;
    let failed = 0;
    const conflicts = []; // dropped because the plant changed elsewhere
    const givenUp = [];   // dropped after exhausting retries
    const events = [];    // sync-history log entries
    try {
      for (const entry of entries) {
        try {
          await axios.request({
            method: entry.method,
            url: `${BACKEND_URL}${entry.url.replace(BACKEND_URL, '')}`,
            data: entry.data ?? undefined,
            headers: { Authorization: `Bearer ${sessionToken}` },
          });
          await removeMutation(entry.key);
          synced += 1;
          setProgress({ done: synced, total: entries.length });
          events.push({ op: entry.op, name: (entry.names && entry.names[0]) || null, status: 'synced' });
        } catch (err) {
          // Went offline mid-flush → stop and keep the rest for later.
          if (err?.code === 'ERR_OFFLINE' || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
            break;
          }
          const status = err?.response?.status;
          if (status === 404 || status === 410 || status === 409) {
            // Plant changed/removed on another device — can't apply.
            await removeMutation(entry.key);
            conflicts.push(entry);
            events.push({ op: entry.op, name: (entry.names && entry.names[0]) || null, status: 'conflict' });
          } else {
            // Retryable (network / 5xx): bump the attempt counter and
            // give up once we hit the cap so it can't loop forever.
            const attempts = (entry.attempts || 0) + 1;
            if (attempts >= MAX_ATTEMPTS) {
              // Undo the optimistic mirror change so the local library
              // stops showing something that never actually saved.
              try { await revertMutationInMirror(userKey, entry.restore || []); } catch { /* non-fatal */ }
              await removeMutation(entry.key);
              givenUp.push(entry);
              events.push({ op: entry.op, name: (entry.names && entry.names[0]) || null, status: 'gaveup' });
            } else {
              await updateMutation(entry.key, { attempts });
              failed += 1;
            }
          }
        }
      }
    } finally {
      flushingRef.current = false;
      setFlushing(false);
      setProgress({ done: 0, total: 0 });
      await refresh();
    }

    if (events.length > 0) recordSyncEvents(userKey, events);

    const L = labelsRef.current;
    if (synced > 0) {
      toast.success(synced === 1
        ? (L.syncedOne || '1 change synced')
        : (L.syncedMany || '{n} changes synced').replace('{n}', String(synced)));
      if (onSyncedRef.current) onSyncedRef.current();
    }
    if (conflicts.length > 0) {
      if (onSyncedRef.current) onSyncedRef.current();
      const firstName = (conflicts[0].names && conflicts[0].names[0]) || null;
      const msg = conflicts.length === 1 && firstName
        ? (L.conflictOne || 'Couldn\u2019t sync "{name}" \u2014 it was changed or removed on another device.').replace('{name}', firstName)
        : (L.conflictMany || '{n} changes couldn\u2019t sync \u2014 those plants were changed or removed on another device.').replace('{n}', String(conflicts.length));
      toast.error(msg, { duration: 6000 });
    }
    if (givenUp.length > 0) {
      // The mirror was reverted per-entry above; refresh the UI to match.
      if (onSyncedRef.current) onSyncedRef.current();
      const firstName = (givenUp[0].names && givenUp[0].names[0]) || null;
      const msg = givenUp.length === 1 && firstName
        ? (L.gaveupOne || 'Gave up syncing "{name}" after several tries \u2014 please redo it.').replace('{name}', firstName)
        : (L.gaveupMany || '{n} changes couldn\u2019t be saved after several tries and were dropped.').replace('{n}', String(givenUp.length));
      toast.error(msg, { duration: 6000 });
    }
    if (failed > 0) {
      toast.error(failed === 1
        ? (L.retryOne || "1 change couldn't sync — will retry")
        : (L.retryMany || "{n} changes couldn't sync — will retry").replace('{n}', String(failed)));
    }
    if (synced > 0) {
      const remaining = await getPendingMutations(userKey);
      if (remaining.length === 0) showSyncedConfirm();
    }
  }, [enabled, userKey, refresh, showSyncedConfirm]);

  // Discard a single pending change before it syncs: undo its optimistic
  // mirror edit, drop it from the queue, and offer an Undo that re-queues
  // and re-applies the change.
  const discard = useCallback(async (entry) => {
    if (!entry) return;
    try {
      await revertMutationInMirror(userKey, entry.restore || []);
    } catch { /* non-fatal */ }
    await removeMutation(entry.key);
    // Pre-mark the offline-cache toast as shown so the queue-driven
    // refresh below doesn't spawn an info toast that covers (and on
    // mobile, blocks) the "Undo" action on the discard toast.
    try { sessionStorage.setItem('offline_cache_toast_shown', '1'); } catch { /* private mode */ }
    await refresh();
    if (onSyncedRef.current) onSyncedRef.current();
    const discardedAt = Date.now();
    recordSyncEvents(userKey, [{ at: discardedAt, op: entry.op, name: (entry.names && entry.names[0]) || null, status: 'discarded' }]);

    const undo = async () => {
      // Roll back the history entry so a re-queued change isn't also
      // shown as "discarded".
      removeSyncEventAt(userKey, discardedAt);
      let restore = entry.restore || [];
      try {
        const res = await applyMutationToMirror(userKey, mutationFromEntry(entry));
        restore = res.restore || restore;
      } catch { /* non-fatal */ }
      await enqueueMutation({
        user_key: entry.user_key || userKey,
        op: entry.op,
        method: entry.method,
        url: entry.url,
        data: entry.data ?? null,
        plant_id: entry.plant_id || null,
        ids: entry.ids || null,
        names: entry.names || [],
        restore,
        attempts: 0,
        count: entry.count || 1,
      });
      await refresh();
      if (onSyncedRef.current) onSyncedRef.current();
    };

    toast.success(labelsRef.current.discarded || 'Change discarded', {
      id: `discard-${entry.key}`,
      duration: 8000,
      action: { label: (labelsRef.current.undo || 'Undo'), onClick: () => { undo(); } },
    });
  }, [userKey, refresh]);

  // Discard every pending change at once: revert each optimistic mirror
  // edit, clear the queue, log the discards, and offer a single Undo that
  // re-queues + re-applies them all.
  const discardAll = useCallback(async () => {
    const entries = await getPendingMutations(userKey);
    if (entries.length === 0) return;
    for (const entry of entries) {
      try { await revertMutationInMirror(userKey, entry.restore || []); } catch { /* non-fatal */ }
    }
    await clearAllMutations(userKey);
    try { sessionStorage.setItem('offline_cache_toast_shown', '1'); } catch { /* private mode */ }
    await refresh();
    if (onSyncedRef.current) onSyncedRef.current();
    const discardedAt = Date.now();
    recordSyncEvents(userKey, entries.map((entry) => ({
      at: discardedAt, op: entry.op, name: (entry.names && entry.names[0]) || null, status: 'discarded',
    })));

    const undoAll = async () => {
      removeSyncEventAt(userKey, discardedAt);
      for (const entry of entries) {
        let restore = entry.restore || [];
        try {
          const res = await applyMutationToMirror(userKey, mutationFromEntry(entry));
          restore = res.restore || restore;
        } catch { /* non-fatal */ }
        await enqueueMutation({
          user_key: entry.user_key || userKey,
          op: entry.op,
          method: entry.method,
          url: entry.url,
          data: entry.data ?? null,
          plant_id: entry.plant_id || null,
          ids: entry.ids || null,
          names: entry.names || [],
          restore,
          attempts: 0,
          count: entry.count || 1,
        });
      }
      await refresh();
      if (onSyncedRef.current) onSyncedRef.current();
    };

    toast.success(
      (labelsRef.current.discardedAll || 'Discarded all pending changes'),
      {
        id: 'discard-all',
        duration: 8000,
        action: { label: (labelsRef.current.undo || 'Undo'), onClick: () => { undoAll(); } },
      },
    );
  }, [userKey, refresh]);

  // Keep the pending list live + auto-flush on reconnect, on mount, and
  // on a quiet background timer while online.
  useEffect(() => {
    if (!enabled) { setPending([]); return; }
    refresh();
    const onChange = () => refresh();
    const onOnline = () => { refresh(); flush(); };
    window.addEventListener(QUEUE_CHANGED_EVENT, onChange);
    window.addEventListener('storage', onChange);
    window.addEventListener('online', onOnline);
    if (typeof navigator === 'undefined' || navigator.onLine !== false) flush();
    const timer = setInterval(() => {
      if (typeof navigator === 'undefined' || navigator.onLine !== false) flush();
    }, AUTO_RETRY_MS);
    return () => {
      window.removeEventListener(QUEUE_CHANGED_EVENT, onChange);
      window.removeEventListener('storage', onChange);
      window.removeEventListener('online', onOnline);
      clearInterval(timer);
      if (syncedTimerRef.current) clearTimeout(syncedTimerRef.current);
    };
  }, [enabled, refresh, flush]);

  return { pending, pendingCount: pending.length, flushing, flush, discard, discardAll, justSynced, progress };
}
